import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { AccessCheckQuery, AccessCheckResult, AccessCheckUserSuggestion } from '../../_models/admin/access-check.models';
import { backgroundContext } from '../../_helpers/loading.interceptor';

/**
 * Typed, read-only call to api/Maintenance/AccessCheck: can this user use this feature, and why not. The API
 * is admin-only, returns 404 while the admin.access-check switch is off and 400 when the user is blank.
 *
 * Follows the shared BaseService pattern: a GET with no body, so prepareOptions(null), which also sets
 * observe:'response', so the response is unwrapped. handleError keeps the status for the panel.
 */
@Injectable({ providedIn: 'root' })
export class AccessCheckService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /** Values left empty are not sent. */
  public check(query: AccessCheckQuery): Observable<AccessCheckResult> {
    let params = new HttpParams();
    (['user', 'feature', 'switchKey', 'role'] as const).forEach(name => {
      const value = (query[name] ?? '').trim();
      if (value) {
        params = params.set(name, value);
      }
    });
    if (query.userId != null) {
      params = params.set('userId', String(query.userId));
    }
    return this.http.get(this.apiUrl + 'api/Maintenance/AccessCheck', { ...this.prepareOptions(null), params }).pipe(
      map((data: any) => this.unwrap(data) as AccessCheckResult),
      catchError(this.handleError('check'))
    );
  }

  /**
   * Suggestions for the user name box: live accounts whose user name, email or name contains q (trimmed). The API
   * returns an empty list under 2 characters, and 404 while the switch is off. A background request, so typing does
   * not show the spinner.
   */
  public searchUsers(q: string): Observable<AccessCheckUserSuggestion[]> {
    const params = new HttpParams().set('q', (q ?? '').trim());
    return this.http.get(this.apiUrl + 'api/Maintenance/AccessCheck/Users',
      { ...this.prepareOptions(null), params, context: backgroundContext() }).pipe(
      map((data: any) => (this.unwrap(data) ?? []) as AccessCheckUserSuggestion[]),
      catchError(this.handleError('searchUsers'))
    );
  }

  /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
